import React, { useState } from 'react';
import { Typography, IconButton, Input, Button, RadioGroup, FormControlLabel, Radio } from '@mui/material';
import Modal from 'styled-react-modal';
import { Formik, FieldArray, ErrorMessage } from 'formik';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { MdRemove } from 'react-icons/md';
import { GrFormAdd } from 'react-icons/gr';
import { useSelector, useDispatch } from 'react-redux';
import { useLoading, ThreeDots } from '@agney/react-loading';
import clsx from 'clsx';
import { editRecipe, updateImage } from '../../redux/actions/recipes';
import { recipeSchema } from '../../services/recipeSchema';
import { useStyles } from './styles';
import './styles.sass';

// firebase
import { doc, updateDoc } from 'firebase/firestore/lite';
import { db } from '../../services';

const EditingDialog = ({ openEditingModal, setOpenEditingModal }) => {
    const [image, setImage] = useState(null);
    const [disabled, setDisabled] = useState(false);
    const recipes = useSelector(state => state.recipes);
    const pickedRecipe = useSelector(state => state.pickedRecipe);
    const dispatch = useDispatch();
    const classes = useStyles();
    const { containerProps, indicatorEl } = useLoading({
        loading: true,
        indicator: <ThreeDots width="30" color="#FFFFFF" />,
    });

    const initialValues = {
        name: pickedRecipe.name,
        quantity: pickedRecipe.quantity,
        category: pickedRecipe.category,
        time: pickedRecipe.time,
        ingredients: pickedRecipe.ingredients,
        directions: pickedRecipe.directions
    };

    const handleClose = () => {
        setOpenEditingModal(false);
        if (image)
            setImage(null);
    }

    const handleImageUpload = (files) => {
        setImage(files[0]);
    }

    const handleDocumentChange = async (index, editedRecipe) => {
        const recipeRef = doc(db, "recipes", pickedRecipe.id);
        try {
            // Update document on Firestore
            await updateDoc(recipeRef, {
                name: editedRecipe.name,
                quantity: editedRecipe.quantity,
                category: editedRecipe.category,
                time: {
                    unit: editedRecipe.time.unit,
                    value: editedRecipe.time.value
                },
                ingredients: editedRecipe.ingredients,
                directions: editedRecipe.directions,
                image: editedRecipe.image
            });
        }
        catch (error) {
            console.log("error: ", error.message);
        }
        finally {
            // Update store
            dispatch(editRecipe(index, editedRecipe));
            dispatch({ type: 'SET_PICKED_RECIPE', pickedRecipe: editedRecipe });
            setOpenEditingModal(false);
            setDisabled(false);
        }
    }

    const onEditRecipe = async (editedRecipe) => {
        setDisabled(true);
        if (editedRecipe.time.value > 1 && editedRecipe.time.unit === 'דקה')
            editedRecipe.time.unit = 'דקות';
        else
            if (editedRecipe.time.value > 1 && editedRecipe.time.unit === 'שעה')
                editedRecipe.time.unit = 'שעות';
        const index = recipes.findIndex(element => element.id === pickedRecipe.id);
        if (image) {
            // Delete old image from Cloudinary
            fetch(`https://cloudinary-management.herokuapp.com/delete-image?public_id=${pickedRecipe.image.public_id}`)
                .then((res) => res.json())
                .then((res) => console.log(res))
                .catch((error) => console.log("error: ", error.message));
            // Upload new image to Cloudinary
            const data = new FormData();
            data.append('file', image);
            data.append('upload_preset', 'cookEat');
            data.append('cloud_name', process.env.REACT_APP_CLOUDINARY_KEY);
            fetch(`https://api.cloudinary.com/v1_1/${process.env.REACT_APP_CLOUDINARY_KEY}/image/upload`, {
                method: 'POST',
                body: data
            })
                .then(res => res.json())
                .then(data => {
                    editedRecipe.image = {
                        url: data.url,
                        public_id: data.public_id
                    };
                    dispatch(updateImage(index, editedRecipe.image));
                    handleDocumentChange(index, editedRecipe);
                })
                .catch((error) => console.log("error on upload image:", error.message));
        }
        else {
            editedRecipe.image = pickedRecipe.image;
            handleDocumentChange(index, editedRecipe);
        }
    }

    const getRecipeTime = () => {
        if (pickedRecipe.time.unit === 'דקות' || pickedRecipe.time.unit === 'דקה')
            return 'דקה';
        return 'שעה';
    }

    return (
        <StyledModal
            isOpen={openEditingModal}
            onBackgroundClick={handleClose}
            onEscapeKeydown={handleClose}
        >
            <div className="header">
                <Typography className={classes.text} variant="h6">עריכת מתכון</Typography>
                <label htmlFor="file-upload" className="custom-file-upload">
                    <img
                        className="preview"
                        src={image ?
                            URL.createObjectURL(image)
                            :
                            pickedRecipe.image.url
                        }
                        alt="Error"
                    />
                </label>
                <input
                    id="file-upload"
                    type="file"
                    accept="image/*"
                    onChange={(event) => handleImageUpload(event.target.files)}
                />
            </div>
            <Formik
                initialValues={initialValues}
                enableReinitialize
                onSubmit={(values) => onEditRecipe(values)}
                validationSchema={recipeSchema}
            >
                {({ handleChange, handleSubmit, values, errors, touched, resetForm }) => {
                    return (
                        <div className="form">
                            <div className="text-input-wrapper mt0">
                                <Typography variant="subtitle1" className={classes.text}>שם המתכון</Typography>
                                <Input
                                    required
                                    autoFocus
                                    value={values.name}
                                    onChange={handleChange('name')}
                                    className={classes.input}
                                    variant="outlined"
                                    placeholder='איך קוראים למנה?'
                                    disableUnderline
                                />
                            </div>
                            <ErrorMessage
                                name='name'
                                render={(message) => {
                                    return (
                                        <div className="error-container">
                                            <WarningAmberRoundedIcon className="icon" />
                                            <Typography className={classes.error}>{message}</Typography>
                                        </div>
                                    )
                                }}
                            />
                            {/* quantity */}
                            <div className="text-input-wrapper">
                                <Typography variant="subtitle1" className={classes.text}>כמות מנות</Typography>
                                <Input
                                    required
                                    value={values.quantity}
                                    onChange={handleChange('quantity')}
                                    className={classes.input}
                                    variant="outlined"
                                    placeholder='לכמה אנשים הוא מספיק?'
                                    disableUnderline
                                />
                            </div>
                            <ErrorMessage
                                name='quantity'
                                render={(message) => {
                                    return (
                                        <div className="error-container">
                                            <WarningAmberRoundedIcon className="icon" />
                                            <Typography className={classes.error}>{message}</Typography>
                                        </div>
                                    )
                                }}
                            />
                            {/* category */}
                            <div className="text-input-wrapper">
                                <Typography variant="subtitle1" className={classes.text}>קטגוריה</Typography>
                                <Input
                                    required
                                    value={values.category}
                                    onChange={handleChange('category')}
                                    className={classes.input}
                                    variant="outlined"
                                    placeholder='לאיזו קטגוריה הוא שייך?'
                                    disableUnderline
                                />
                            </div>
                            <ErrorMessage
                                name='category'
                                render={(message) => {
                                    return (
                                        <div className="error-container">
                                            <WarningAmberRoundedIcon className="icon" />
                                            <Typography className={classes.error}>{message}</Typography>
                                        </div>
                                    )
                                }}
                            />
                            {/* time */}
                            <div className="text-input-wrapper">
                                <Typography variant="subtitle1" className={classes.text}>זמן הכנה</Typography>
                                <div className="time-wrapper">
                                    <Input
                                        required
                                        value={values.time.value}
                                        onChange={handleChange('time.value')}
                                        className={classes.input}
                                        variant="outlined"
                                        placeholder='בכמה זמן מכינים אותו?'
                                        disableUnderline
                                    />
                                    <RadioGroup
                                        row
                                        defaultValue={getRecipeTime()}
                                        onChange={handleChange('time.unit')}
                                        className={classes.radioGroup}
                                    >
                                        <FormControlLabel
                                            value='דקה'
                                            control={<Radio />}
                                            label={<Typography className={classes.text}>דקה</Typography>}
                                        />
                                        <FormControlLabel
                                            value='שעה'
                                            control={<Radio />}
                                            label={<Typography className={classes.text}>שעה</Typography>}
                                        />
                                    </RadioGroup>
                                </div>
                            </div>
                            <ErrorMessage
                                name='category'
                                render={(message) => {
                                    return (
                                        <div className="error-container">
                                            <WarningAmberRoundedIcon className="icon" />
                                            <Typography className={classes.error}>{message}</Typography>
                                        </div>
                                    )
                                }}
                            />
                            {/* ingredients */}
                            <Typography variant='h6' className={clsx(classes.text, classes.label)}>מרכיבים</Typography>
                            <FieldArray
                                name='ingredients'
                                render={(arrayHelpers) => {
                                    const ingredients = values.ingredients;
                                    return (
                                        ingredients.map((ingredient, index) => {
                                            return (
                                                <div key={index}>
                                                    <div className='dynamic-input-wrapper'>
                                                        <div className='text-input-wrapper mb0'>
                                                            <Input
                                                                placeholder={`מרכיב ${index + 1}`}
                                                                value={values.ingredients[index].title}
                                                                onChange={handleChange(`ingredients.${index}.title`)}
                                                                className={classes.input}
                                                                variant="outlined"
                                                                disableUnderline
                                                            />
                                                        </div>
                                                        <div className='text-input-wrapper mb0 mh5'>
                                                            <Input
                                                                placeholder='כמות'
                                                                value={values.ingredients[index].amount}
                                                                onChange={handleChange(`ingredients.${index}.amount`)}
                                                                className={classes.input}
                                                                variant="outlined"
                                                                disableUnderline
                                                            />
                                                        </div>
                                                        <div className='text-input-wrapper mb0'>
                                                            <Input
                                                                placeholder='יחידה'
                                                                value={values.ingredients[index].unit}
                                                                onChange={handleChange(`ingredients.${index}.unit`)}
                                                                className={classes.input}
                                                                variant="outlined"
                                                                disableUnderline
                                                            />
                                                        </div>
                                                        {ingredients.length - 1 === index &&
                                                            <IconButton
                                                                onClick={() => arrayHelpers.push({ title: '', amount: '', unit: '' })}
                                                                className='action-button'
                                                                size='small'
                                                            >
                                                                <GrFormAdd className='add' />
                                                            </IconButton>
                                                        }
                                                        {ingredients.length > 1 &&
                                                            <IconButton
                                                                onClick={() => arrayHelpers.remove(index)}
                                                                className='action-button'
                                                                size='small'
                                                            >
                                                                <MdRemove className='remove' />
                                                            </IconButton>
                                                        }
                                                    </div>
                                                    {touched.ingredients && errors.ingredients && errors.ingredients[index] &&
                                                        <ErrorMessage
                                                            name={`ingredients.${index}.title`}
                                                            render={(message) => {
                                                                return (
                                                                    <div className="error-container">
                                                                        <WarningAmberRoundedIcon className="icon" />
                                                                        <Typography className={classes.error}>{message}</Typography>
                                                                    </div>
                                                                )
                                                            }}
                                                        />
                                                    }
                                                    {touched.ingredients && errors.ingredients && errors.ingredients[index] &&
                                                        <ErrorMessage
                                                            name={`ingredients.${index}.amount`}
                                                            render={(message) => {
                                                                return (
                                                                    <div className="error-container">
                                                                        <WarningAmberRoundedIcon className="icon" />
                                                                        <Typography className={classes.error}>{message}</Typography>
                                                                    </div>
                                                                )
                                                            }}
                                                        />
                                                    }
                                                    {touched.ingredients && errors.ingredients && errors.ingredients[index] &&
                                                        <ErrorMessage
                                                            name={`ingredients.${index}.unit`}
                                                            render={(message) => {
                                                                return (
                                                                    <div className="error-container">
                                                                        <WarningAmberRoundedIcon className="icon" />
                                                                        <Typography className={classes.error}>{message}</Typography>
                                                                    </div>
                                                                )
                                                            }}
                                                        />
                                                    }
                                                </div>
                                            )
                                        })
                                    );
                                }}
                            />
                            {/* directions */}
                            <Typography variant='h6' className={clsx(classes.text, classes.label)}>הוראות הכנה</Typography>
                            <FieldArray
                                name='directions'
                                render={(arrayHelpers) => {
                                    const directions = values.directions;
                                    return (
                                        directions.map((direction, index) => (
                                            <div style={{ width: '100%' }} key={index}>
                                                <div className="dynamic-input-wrapper">
                                                    <div className='text-input-wrapper mb0'>
                                                        <Input
                                                            placeholder={`הוראה ${index + 1}`}
                                                            value={values.directions[index]}
                                                            onChange={handleChange(`directions.${index}`)}
                                                            className={classes.input}
                                                            variant="outlined"
                                                            disableUnderline
                                                            multiline
                                                        />
                                                    </div>
                                                    {directions.length - 1 === index &&
                                                        <IconButton
                                                            onClick={() => arrayHelpers.push('')}
                                                            className='action-button'
                                                            size='small'
                                                        >
                                                            <GrFormAdd className='add' />
                                                        </IconButton>
                                                    }
                                                    {directions.length > 1 &&
                                                        <IconButton
                                                            onClick={() => arrayHelpers.remove(index)}
                                                            className='action-button'
                                                            size='small'
                                                        >
                                                            <MdRemove className='remove' />
                                                        </IconButton>
                                                    }
                                                </div>
                                                {touched.directions && errors.directions &&
                                                    <ErrorMessage
                                                        name={`directions.${index}`}
                                                        render={(message) => {
                                                            return (
                                                                <div className="error-container">
                                                                    <WarningAmberRoundedIcon className="icon" />
                                                                    <Typography className={classes.error}>{message}</Typography>
                                                                </div>
                                                            )
                                                        }}
                                                    />
                                                }
                                            </div>
                                        ))
                                    );
                                }}
                            />
                            <div className="buttons">
                                <Button
                                    onClick={handleSubmit}
                                    className={clsx(classes.button, classes.add)}
                                    disabled={disabled}
                                >
                                    {!disabled ?
                                        'עריכה'
                                        :
                                        <section className="indicator" {...containerProps}>
                                            {indicatorEl}
                                        </section>
                                    }
                                </Button>
                            </div>
                        </div>
                    );
                }}
            </Formik>
        </StyledModal>
    )
}

const StyledModal = Modal.styled`
    width: 450px;
    height: 450px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    border-radius: 25px;
    background-color: #000f14;
    padding: 20px;
    cursor: default;

    @media (max-width: 960px) {
        height: 100%;
    }

    @media (max-width: 400px) {
        width: 100%;
    }
`;

export default EditingDialog;